<template>
  <v-container class="pa-4 pa-md-8">
    <v-row>
      <v-col cols="12" sm="12" md="4">
        <v-card class="overflow-hidden" color="primary">
          <font-awesome-icon
            class="accent-icon white--text"
            icon="envelope"
          ></font-awesome-icon>
          <v-card-text class="text-right white--text">
            <div class="text-h3 text-md-h4 text-lg-h3">
              {{ invitees | number }}
            </div>
            <div class="text-h6 text-md-subtitle-1 text-md-h6">
              Invitees
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="12" md="4">
        <v-card class="overflow-hidden" color="secondary darken-2">
          <font-awesome-icon
            style="left: -1rem;"
            class="accent-icon white--text"
            :icon="['far', 'star']"
          ></font-awesome-icon>
          <v-card-text class="text-right white--text">
            <div class="text-h3 text-md-h4 text-lg-h3">
              {{ responses | number }}
            </div>
            <div class="text-h6 text-md-subtitle-1 text-md-h6">Respondents</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="12" md="4">
        <v-card class="overflow-hidden" color="accent darken-2">
          <font-awesome-icon
            style="transform: rotate(0); left: 1rem;"
            class="accent-icon white--text"
            icon="chart-line"
          ></font-awesome-icon>
          <v-card-text class="text-right white--text">
            <div class="text-h3 text-md-h4 text-lg-h3">
              {{ (responses / invitees) | percentage }}
            </div>
            <div class="text-h6 text-md-subtitle-1 text-md-h6">
              Response Rate
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" lg="9" xl="9">
        <v-card>
          <v-card-title class="d-flex align-center flex-md-row flex-column">
            <div class="d-flex flex-fill align-center w-100">
              <span class="text-h5 text-md-h4 flex-fill text-truncate"
                >District &amp; School Results</span
              >

              <v-btn
                @click.stop="displayMode = 0"
                elevation="0"
                :outlined="displayMode != 0"
                :color="`grey ${displayMode == 0 ? 'lighten-2' : 'darken-2'}`"
                class="mr-2"
              >
                <font-awesome-icon
                  icon="table"
                  class="mr-2"
                ></font-awesome-icon>
                Grid
              </v-btn>
              <v-btn
                @click="displayMode = 1"
                elevation="0"
                :outlined="displayMode != 1"
                :color="`grey ${displayMode == 1 ? 'lighten-2' : 'darken-2'}`"
              >
                <font-awesome-icon icon="bars" class="mr-2"></font-awesome-icon>
                List
              </v-btn>
            </div>

            <v-text-field
              class="mt-3 w-100"
              @input="debounceSearch"
              type="search"
              placeholder="Search Districts and Schools"
              filled
              rounded
              dense
              hide-details
              prepend-inner-icon="mdi-magnify"
            ></v-text-field>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-alert color="secondary lighten-3 mb-0" border="left">
              <div class="d-flex align-top">
                <v-icon class="mr-2 ml-2">$info</v-icon>
                <span
                  >Reports are not available for districts and schools below
                  minimum response rates of {{ displayPercentage }} and at least
                  5 respondents.</span
                >
              </div>
            </v-alert>
            <div class="mt-3 px-4 pt-3 pb-4 grey lighten-3 rounded mb-8 ">
              <div class="text-subtitle-1 font-weight-bold">
                Response Rate Key
              </div>
              <v-divider class="mb-2 mt-1"></v-divider>
              <div class="d-flex  flex-wrap align-center">
                <v-chip
                  v-for="(code, index) in barColor"
                  :key="index"
                  :color="code.color"
                  class="font-weight-bold mr-2 my-1"
                  >{{ code.text }}</v-chip
                >
              </div>
            </div>
          </v-card-text>
          <grid
            v-show="displayMode === 0"
            :items="flattenedResponseRates"
            :search="search"
            :disable-report="disableReport"
            :reports="reports"
            @toggle-report="(data) => $emit('toggle-report', data)"
          ></grid>
          <list
            v-show="displayMode === 1"
            :items="responseRates"
            :search="search"
            :disable-report="disableReport"
            :reports="reports"
            @toggle-report="(data) => $emit('toggle-report', data)"
          ></list>
        </v-card>
      </v-col>
      <v-col cols="12" lg="3" xl="3">
        <v-card color="white" class="d-flex align-center d-lg-block">
          <v-card-title class="pb-lg-2 flex-fill">State Reports</v-card-title>
          <v-divider class="d-none d-lg-block"></v-divider>
          <div class="flex-column">
            <v-btn
              text
              v-for="report in reports"
              :key="report.label"
              class="w-100 justify-start rounded-0"
              @click="
                $emit('toggle-report', {
                  districtId: null,
                  schoolId: null,
                  name: report.name,
                  settings: report.settings,
                })
              "
            >
              <font-awesome-icon
                class="mr-2"
                v-if="report.icon"
                :icon="report.icon"
              ></font-awesome-icon>
              {{ report.label }}
            </v-btn>
            <v-btn
              v-if="surveyId == '2024_NCTWCS'"              
              text
              class="w-100 justify-start rounded-0"
              download
              href="/reports/nctwcs/NCTWCS_2024_AgreeComparison_Report.xlsx"
            >
              <font-awesome-icon
                class="mr-2"
                icon="bars"
              ></font-awesome-icon>
              Agree Comparison
            </v-btn>
          </div>
        </v-card>
        <v-card
          v-if="principalReports && usePrincipalReports"
          color="white"
          class="d-flex align-center d-lg-block mt-5"
        >
          <v-card-title class="pb-lg-2 flex-fill"
            >Principal Reports</v-card-title
          >
          <v-divider class="d-none d-lg-block"></v-divider>
          <v-card-actions class="justify-end">
            <v-tooltip
              top
              v-for="report in principalReports"
              :key="report.label"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  icon
                  large
                  v-bind="attrs"
                  v-on="on"
                  @click="
                    $emit('toggle-report', {
                      districtId: null,
                      schoolId: null,
                      name: report.name,
                      settings: report.settings,
                    })
                  "
                  ><font-awesome-icon
                    v-if="report.icon"
                    :icon="report.icon"
                  ></font-awesome-icon
                ></v-btn>
              </template>
              <span>{{ report.label }}</span>
            </v-tooltip>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
.accent-icon {
  font-size: 7rem;
  position: absolute;
  top: 0rem;
  left: 0.5rem;
  transform: rotate(-20deg);
  opacity: 0.35;
}

.rotate-180 {
  transform: rotate(180deg);
}

.willTransform {
  transition: transform 250ms ease-in-out;
}

.scroller {
  overflow: auto;
  margin-bottom: 50px;
  height: 600px;
}
</style>

<script>
import numeral from "numeral";
import debounce from "debounce";

import Grid from "./Results.ResponseRate.Grid.vue";
import List from "./Results.ResponseRate.List.vue";

import { thresholdByYear } from "../../../constants/Survey";

export default {
  components: {
    Grid,
    List,
  },
  filters: {
    percentage(val) {
      return numeral(val).format("0.00%");
    },
  },
  props: {
    codeStats: Array,
    reports: Object,
    principalReports: Object,
  },
  data: () => ({
    pendingStateChanges: 0,
    survey: null,
    schools: [],
    districts: [],
    responseRates: [],
    flattenedResponseRates: [],
    // Control States
    displayMode: 1,
    search: "",
    barColor: [
      { text: "100%", color: "threshold-colors-1 elevation-2" },
      {
        text: "Greater than or equal to 75%",
        color: "threshold-colors-2 white--text elevation-2",
      },
      {
        text: "Greater than or equal to 50%",
        color: "threshold-colors-3 white--text elevation-2",
      },
      {
        text: "Greater than or equal to 35%",
        color: "threshold-colors-4 elevation-2",
      },
      {
        text: "Less than 35%",
        color: "threshold-colors-5 white--text elevation-2",
      },
    ],
  }),
  computed: {
    // barColor() {
    //   if (this.percentage == 1) return "green";
    //   if (this.percentage >= 0.75) return "primary";
    //   if (this.percentage >= 0.5) return "secondary darken-2";
    //   if (this.percentage >= 0.35) return "orange darken-2";
    //   else return "red darken-2";
    // },
    displayPercentage() {
      let percentageFloor =
        thresholdByYear.find((x) => x.surveyId === this.surveyId)
          .percentageFloor * 100;
      let displayPercentage = `${percentageFloor.toFixed(0)}%`;
      return displayPercentage;
    },
    usePrincipalReports() {
      return thresholdByYear.find((x) => x.surveyId === this.surveyId)
        .usePrincipalReports;
    },
    tenantId() {
      return () => "NC_DPI";
    },
    surveyId() {
      return this.$route.params.id;
    },
    entities() {
      return this.$store.state.entities.data;
    },
    invitees() {
      return this.codeStats.reduce((agg, x) => agg + x.total, 0);
    },
    responses() {
      return this.codeStats.reduce((agg, x) => agg + x.used, 0);
    },
  },
  watch: {
    entities() {
      this.setResponseRates();
    },
    responseRates() {
      this.populate(this.responseRates, 0);
    },
  },
  methods: {
    setResponseRates() {
      let getRates = (entities) => {
        var results = [];

        for (let i = 0; i < entities.length; i++) {
          let result = {};
          if (entities[i].children?.length) {
            result.children = getRates(entities[i].children);
            result.id = entities[i].id;
            result.name = entities[i].name;
            result.invitees = result.children.reduce(
              (agg, x) => agg + x.invitees,
              0
            );
            result.responses = result.children.reduce(
              (agg, x) => agg + x.responses,
              0
            );
          } else {
            let stat = this.codeStats.find((x) => {
              var entityId = x.metaData.find((y) => y.key === "EntityId")
                ?.value;

              return entities[i].id === entityId;
            });

            if (stat) {
              result.id = entities[i].id;
              result.name = entities[i].name;
              result.invitees = stat.total;
              result.responses = stat.used;
            }
          }

          if (result.id) results.push(result);
        }

        return results;
      };

      this.responseRates = getRates(this.entities);

      this.flattenedResponseRates = [].concat.apply(
        [],
        this.responseRates.slice().map((x) =>
          x.children
            ? x.children.map((y) => {
                y.districtId = x.id;
                y.districtName = x.name;
                y.responseRate = numeral(y.responses / y.invitees).format(
                  "0.00%"
                );
                return y;
              })
            : [
                {
                  districtId: x.id,
                  districtName: x.name,
                  ...x,
                  responseRate: numeral(x.responses / x.invitees).format(
                    "0.00%"
                  ),
                },
              ]
        )
      );
    },
    populate(arr) {
      for (let i = 0; i < arr.length; i++) {
        if (!arr[i].children) {
          this.schools.push(arr[i]);
        }

        if (arr[i].children) {
          this.districts.push(arr[i]);
          this.populate(arr[i].children);
        }
      }
    },
    debounceSearch: debounce(function(e) {
      this.search = e;
    }, 250),
    disableReport(item) {
      let floor = thresholdByYear.find((x) => x.surveyId === this.surveyId)
        .percentageFloor;

      return item.responses < 5 || item.responses / item.invitees < floor;
    },
  },
  mounted() {
    this.setResponseRates();
    this.populate(this.responseRates, 0);
  },
};
</script>
